import {AppUrls} from 'shared/config/model/urls';

import {MouseEvent, useCallback} from 'react';
import {generatePath, useNavigate} from 'react-router-dom';

import {MonitorRowClickHandler, useMonitorRowSelection} from 'entities/monitor';
import {
    getPortMonitorAddress,
    getPortMonitorDateFormat,
    getPortMonitorName,
    PortMonitorModalType,
} from 'features/port/lib/portMonitorHelper';
import useToggleTagFilter from 'shared/lib/hooks/useToggleTagFilter';
import {isTranslate} from 'shared/lib/lang';

import {IPortMonitorListItem} from './PortMonitor/IPortMonitor';
import {portMonitorSelectedIdsSelector, toggleOne} from './portMonitorSlice';
import usePortMonitorActions from './usePortMonitorActions';
import usePortMonitorToggle from './usePortMonitorToggle';

export interface IUsePortMonitorRow {
    activeModal: PortMonitorModalType,
    closeModal: () => void,
    canCreate: boolean,
    isSelected: boolean,
    isPaused: boolean,
    name: string,
    address: string,
    tags: NonNullable<IPortMonitorListItem['tags']>,
    dateFormat: string,
    onCheckboxChange: () => void,
    onTagClick: (_tag: string) => void,
    onDetailsClick: MonitorRowClickHandler,
    onToggleClick: (_event: MouseEvent) => Promise<void>,
    onCheckClick: MonitorRowClickHandler,
    onEditClick: MonitorRowClickHandler,
    onTagsClick: MonitorRowClickHandler,
    onPauseClick: MonitorRowClickHandler,
    onResumeClick: MonitorRowClickHandler,
    onDeleteClick: MonitorRowClickHandler,
}

export const usePortMonitorRow = (item: IPortMonitorListItem): IUsePortMonitorRow => {
    const navigate = useNavigate();
    const onTagClick = useToggleTagFilter();
    const {isSelected, onCheckboxChange} = useMonitorRowSelection(item.uuid, portMonitorSelectedIdsSelector, toggleOne);
    const {activeModal, canCreate, openModal, closeModal, checkNow} = usePortMonitorActions(item.uuid);
    const onToggleClick = usePortMonitorToggle(item, useCallback(() => openModal('limit'), [openModal]));

    const withStop = (action: () => void): MonitorRowClickHandler => (event) => {
        event.stopPropagation();
        action();
    };

    const onDetailsClick = withStop(() => {
        navigate(generatePath(AppUrls.PORT_MONITOR_DETAILS, {portMonitorId: item.uuid}));
    });

    return {
        activeModal,
        closeModal,
        canCreate,
        isSelected,
        isPaused: item.status === 'paused',
        name: getPortMonitorName(item),
        address: getPortMonitorAddress(item.ip, item.port),
        tags: item.tags ?? [],
        dateFormat: getPortMonitorDateFormat(isTranslate()),
        onCheckboxChange,
        onTagClick,
        onDetailsClick,
        onToggleClick,
        onCheckClick: withStop(checkNow),
        onEditClick: withStop(() => openModal('edit')),
        onTagsClick: withStop(() => openModal('tags')),
        onPauseClick: withStop(() => openModal('pause')),
        onResumeClick: withStop(() => openModal('resume')),
        onDeleteClick: withStop(() => openModal('delete')),
    };
};

export default usePortMonitorRow;
